import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

interface SupportChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleChat: () => void;
  pendingConversationId: string | null;
  openToConversation: (conversationId: string) => void;
  clearPendingConversation: () => void;
  viewingConversationId: string | null;
  setViewingConversationId: (id: string | null) => void;
}

const SupportChatContext = createContext<SupportChatContextType | undefined>(undefined);

export function useSupportChatContext() {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error("useSupportChatContext must be used within a SupportChatProvider");
  }
  return context;
}

export function SupportChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);
  const [viewingConversationId, setViewingConversationId] = useState<string | null>(null);

  const toggleChat = () => setIsOpen((prev) => !prev);

  const openToConversation = useCallback((conversationId: string) => {
    setPendingConversationId(conversationId);
    setIsOpen(true);
  }, []);

  const clearPendingConversation = useCallback(() => {
    setPendingConversationId(null);
  }, []);

  return (
    <SupportChatContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleChat,
        pendingConversationId,
        openToConversation,
        clearPendingConversation,
        viewingConversationId,
        setViewingConversationId,
      }}
    >
      {children}
    </SupportChatContext.Provider>
  );
}
